import React, { useEffect, useState } from 'react';
import Modal from '../../components/Shell/Modal';
import useModal from '../../hooks/useModal';
import { useNavigate } from 'react-router-dom';
import {
	CheckCircleIcon,
	ChevronRightIcon,
	EllipsisVerticalIcon,
	ServerIcon,
	ServerStackIcon,
	XCircleIcon,
} from '@heroicons/react/24/outline';
import {
	TemplateBoolean,
	TemplateCheckbox,
	TemplateCreateable,
	TemplateDropdown,
	TemplateField,
	TemplateFile,
	TemplateReactSelect,
} from '../../components/Attributes/Attributes_old';
import usePrevious from '../../hooks/usePrevious';
import useAsync from '../../hooks/useAsync';
import {
	getModuleCategories,
	getModuleFieldsBeta,
	updateModuleFieldActive,
} from '../../api/modules';
import Button from '../../components/Button';
import Attributes, { configurePayload } from '../../components/Attributes';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import { FIELD_OPTIONS } from '../../components/helpers/constants';
import { useRequiredBeta } from '../../hooks/useRequired';
import { getDocuments } from '../../api/documents';
import moment from 'moment';
import { getTeam } from '../../api/company';
import { Menu } from '@headlessui/react';
import GeneralStepAsset from './steps/GeneralStepAsset';
import AttributesStepAsset from './steps/AttributesStepAsset';
import { addAssets } from '../../api';
import VendorsStepAsset from './steps/VendorsStepAsset';
import PowerUsersStepAsset from './steps/PowerUsersStepAsset';

const MODAL_NAME = 'assetModalForm';
const ASSET_MODULE_ID = process.env.REACT_APP_MODULE_ID_ASSETS;
const SERVICE_MODULE_ID = process.env.REACT_APP_MODULE_ID_SERVICE;
const EVENT_MODULE_ID = process.env.REACT_APP_MODULE_ID_EVENT;

const DEFAULT_NOTIFICATION_FORM = { time: 30, admin: true };

const STATUSES = {
	complete: 'text-green-700 bg-green-50 ring-green-600/20',
	default: 'text-gray-600 bg-gray-50 ring-gray-500/10',
	pending: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20',
};

const AddAssetModal = () => {
	const navigate = useNavigate();
	const [currentFormStep, setCurrentFormStep] = useState(0);
	const [form, setForm] = useState({});
	const [updatedForm, setUpdateForm] = useState({ taskSchedule: [] });
	const [taskForm, setTaskForm] = useState({});
	const [notificationForm, setNotificationForm] = useState(
		DEFAULT_NOTIFICATION_FORM
	);
	const [customAttributeForm, setCustomAttributeForm] = useState({});
	const [acceptedFiles, setAcceptedFiles] = useState(null);

	const [seenAttributes, setSeenAttributes] = useState(false); // hack: redo

	const { open: manageAttributesSettings, setOpen: openManageAttributes } =
		useModal('manageAttributes');

	const [isAddTaskModalOpen, setAddTaskModal] = useState(false);
	const [isAddCustomAttributesModalOpen, setAddCustomAttributesModal] =
		useState(false);

	const { open: options, setClose } = useModal(MODAL_NAME);

	const [currentStep, setStep] = useState(1);
	const { open: addCategoryOpen, setOpen: setAddCategoryOpen } =
		useModal('addCategoryModal');

	const {
		value: moduleFields,
		status: isLoadingModuleFields,
		execute: onRefreshModuleFields,
	} = useAsync(getModuleFieldsBeta, {
		defaultPayload: { id: ASSET_MODULE_ID, active: true },
	});

	const { execute: onSetFieldActive } = useAsync(updateModuleFieldActive, {
		immediate: false,
	});

	const { value: taskModuleFields, status: isLoadingTaskModuleFields } =
		useAsync(getModuleFieldsBeta, {
			defaultPayload: { id: SERVICE_MODULE_ID, active: true },
		});

	const {
		value: categories,
		execute: onRefreshCategories,
		status: isLoadingCategories,
	} = useAsync(getModuleCategories, {
		immediate: false,
	});
	const {
		value: documents,
		execute: onRefreshDocuments,
		status: isLoadingDocuments,
	} = useAsync(getDocuments, {
		immediate: false,
		defaultPayload: {
			pageSize: null,
		},
	});

	const {
		value: eventCategories,
		execute: onRefreshEventCategories,
		status: isLoadingEventCategories,
	} = useAsync(getModuleCategories, { immediate: false });

	const { execute: onAddAsset, status: addingAsset } = useAsync(addAssets, {
		immediate: false,
		showNotification: { error: 'raw' },
	});

	const { value: teamMembers } = useAsync(getTeam);

	const prevManageAttributes = usePrevious(manageAttributesSettings);
	useEffect(() => {
		if (!manageAttributesSettings && prevManageAttributes) {
			onRefreshModuleFields(
				{ categoryId: (updatedForm?.category || form?.category)?.value?.value },
				null,
				{ silent: true }
			);
		}
	}, [manageAttributesSettings]);

	const previousCategory = usePrevious(form?.category?.value?.value);
	useEffect(() => {
		if (
			form?.category?.value?.value &&
			previousCategory !== form?.category?.value?.value
		) {
			onRefreshModuleFields(
				{ categoryId: form?.category?.value?.value },
				null,
				{ silent: true }
			);
		}
	}, [form?.category?.value?.value]);

	const prevOptions = usePrevious(options);
	useEffect(() => {
		if (options && !prevOptions) {
			onRefreshCategories({
				id: ASSET_MODULE_ID,
			});

			if (options?.assetId) {
				setUpdateForm({
					...updatedForm,
					assets: { value: [{ value: options?.assetId }] },
				});
			}

			onRefreshDocuments();
		}

		if (!options && prevOptions) {
			setForm({});
			setUpdateForm({ taskSchedule: [] });
			setStep(1);
			setCurrentFormStep(0);
			setTaskForm({});
		}
	}, [options]);

	const prevIsAddTaskModalOpen = usePrevious(isAddTaskModalOpen);
	const prevTaskModuleFields = usePrevious(taskModuleFields);
	useEffect(() => {
		if (currentFormStep === 2) {
			onRefreshEventCategories({
				id: EVENT_MODULE_ID,
			});
		}
	}, [currentFormStep]);

	useEffect(() => {
		if (isAddTaskModalOpen && taskModuleFields) {
			let obj = {};
			taskModuleFields?.standard
				?.filter((field) => field?.defaultValue)
				?.map((field) => {
					obj[field?.key] = { fieldId: field?.id, value: field?.defaultValue };
				});

			setTaskForm({ ...taskForm, ...obj });
		}
	}, [taskModuleFields, isAddTaskModalOpen]);

	useEffect(() => {
		if (
			currentFormStep === steps.findIndex((o) => o.key === 'customAttributes')
		) {
			setSeenAttributes(true);
		}
	}, [currentFormStep]);

	const previousCoverageStart = usePrevious(
		updatedForm['coverage-start']?.value
	);
	useEffect(() => {
		if (previousCoverageStart !== updatedForm['coverage-start']) {
			setUpdateForm({
				...updatedForm,
				'coverage-end': {
					...(updatedForm['coverage-end'] || {
						fieldId: moduleFields?.combined?.find(
							(o) => o.key === 'coverage-end' && o.isDefault
						)?.id,
					}),
					value: moment(updatedForm['coverage-start']?.value)
						.add(1, 'year')
						.subtract(1, 'day')
						.toDate(),
				},
			});
		}
	}, [updatedForm['coverage-start']?.value]);

	const id = null;
	const skipModuleKeys = [];
	const skipTaskModuleKeys = [
		'name',
		'description',
		'assets',
		'defaultEventStatus',
	];

	const hasAllRequired = useRequiredBeta(
		{ ...form, ...updatedForm },
		moduleFields?.combined
			?.filter((field) => field.isRequired)
			.map((field) => field.key)
	);

	const steps = [
		{ name: 'General', key: 'general' },
		{ name: 'Attributes', key: 'customAttributes' },
		//{ name: 'Contracts' },
		//{ name: 'Tasks' },
		{ name: 'Vendors' },
		{ name: 'Power Users' },
	];

	const moduleFieldsBreakdown = {
		general: [
			...(moduleFields?.standard?.filter(
				(field) =>
					field?.isDefault &&
					[
						'manufacturer',
						'model',
						'serialNumber',
						'location',
						'category',
						'tags',
					].includes(field?.key)
			) || []),
		],
		assets: moduleFields?.standard?.filter(
			(field) => field?.isDefault && ['assets'].includes(field?.key)
		),
		vendor: moduleFields?.standard?.filter(
			(field) => field?.isDefault && ['vendor'].includes(field?.key)
		),
		customAttributes:
			moduleFields?.combined?.filter((field) => !field?.isDefault) || [],
	};

	const OCCURRENCES_OPTIONS = taskModuleFields?.standard?.find(
		(field) => field?.isDefault && field?.key === 'occurrences'
	)?.options?.options;

	return (
		<>
			<Modal
				title={
					currentStep === 1
						? 'Create a new asset'
						: currentStep === 2
						? 'Upload PDF (optional)'
						: currentStep === 3
						? ''
						: ''
				}
				betaTitle
				open={!!options}
				onClose={() => {
					setClose({ onClose: options?.onClose });
				}}
				size={'6xl'}
				preventBackdropClose
				zIndex={40}
			>
				<div className="grid grid-cols-12 gap-x-4">
					{/* 
				
							Step 3.1: Sidebar
							
						*/}
					<div className="col-span-3 my-5">
						<nav aria-label="Progress" className="flex">
							<ol role="list" className="space-y-6">
								{steps.map((step, stepIdx) => {
									const hasRequiredForStep = step?.key
										? useRequiredBeta(
												{ ...form, ...updatedForm },
												[...moduleFieldsBreakdown[step?.key]]
													?.filter((field) => field.isRequired)
													.map((field) => field.key)
										  )
										: false;

									return (
										<li key={stepIdx}>
											{/* CHECK MARKS */}
											{currentFormStep === stepIdx ? (
												<a
													href={step.href}
													aria-current="step"
													className="flex items-center"
												>
													{/* ACTIVE */}

													<span
														aria-hidden="true"
														className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
													>
														<span className="absolute h-4 w-4 rounded-full bg-indigo-200" />
														<span className="relative block h-2 w-2 rounded-full bg-indigo-600" />
													</span>
													<span className="ml-3 text-xs font-medium text-indigo-600">
														{step.name}
													</span>
												</a>
											) : (
												<a
													href={step.href}
													className="group cursor-pointer"
													onClick={() => setCurrentFormStep(stepIdx)}
												>
													<div className="flex items-center">
														<div
															aria-hidden="true"
															className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
														>
															<div className="h-2 w-2 rounded-full bg-gray-300 group-hover:bg-gray-400" />
														</div>
														<p className="ml-3 text-xs font-medium text-gray-500 group-hover:text-gray-900">
															{step.name}
														</p>
													</div>
												</a>
											)}
										</li>
									);
								})}
							</ol>
						</nav>
					</div>

					{/* 
				
							Step 3.1: Main Content
							
						*/}
					<div className="col-span-9">
						<div className="grid gap-y-4 pt-1.5">
							<>
								{isLoadingModuleFields === 'pending' ? (
									<LoadingSkeleton />
								) : (
									<>
										<div className=" min-h-72">
											<div className={'space-y-4'}>
												{/* 
				
													Step 3.11: Main Content - General Details
													
												*/}

												{currentFormStep === 0 && (
													<GeneralStepAsset
														moduleFieldsBreakdown={moduleFieldsBreakdown}
														skipModuleKeys={skipModuleKeys}
														form={form}
														setForm={setForm}
														updatedForm={updatedForm}
														setUpdateForm={setUpdateForm}
													/>
												)}

												{currentFormStep === 1 && (
													<AttributesStepAsset
														moduleFieldsBreakdown={moduleFieldsBreakdown}
														skipModuleKeys={skipModuleKeys}
														form={form}
														setForm={setForm}
														updatedForm={updatedForm}
														setUpdateForm={setUpdateForm}
													/>
												)}

												{currentFormStep === 2 && (
													<VendorsStepAsset
														moduleFieldsBreakdown={moduleFieldsBreakdown}
														skipModuleKeys={skipModuleKeys}
														form={form}
														setForm={setForm}
														updatedForm={updatedForm}
														setUpdateForm={setUpdateForm}
													/>
												)}

												{currentFormStep === 3 && (
													<PowerUsersStepAsset
														moduleFieldsBreakdown={moduleFieldsBreakdown}
														skipModuleKeys={skipModuleKeys}
														form={form}
														setForm={setForm}
														updatedForm={updatedForm}
														setUpdateForm={setUpdateForm}
													/>
												)}
											</div>
										</div>
									</>
								)}
							</>

							<div className="flex justify-end mt-4">
								{currentFormStep + 1 !== steps?.length && (
									<Button
										onClick={() => {
											setCurrentFormStep(currentFormStep + 1);
										}}
										type="ghost"
									>
										{`Optional: Add ${steps[currentFormStep + 1]?.name}`}
									</Button>
								)}

								<Button
									disabled={!hasAllRequired}
									onClick={() => {
										onAddAsset(
											configurePayload(
												updatedForm,
												moduleFields?.combined,
												{
													bucket: 'assets',
												},
												{
													vendors: updatedForm?.vendors?.map((v) => v?.id),
													powerUsers: updatedForm?.powerUsers?.map((o) => ({
														id: o?.id,
														type: o?.type,
													})),
												}
											),
											(payload) => {
												navigate(`/assets/${payload?.id}`);
											}
										);
									}}
								>
									Create asset
								</Button>
							</div>
						</div>
					</div>
				</div>
			</Modal>

			<Modal
				title="Add scheduled task"
				open={isAddTaskModalOpen}
				onClose={() => setAddTaskModal(false)}
				size="xl"
			>
				<>
					{isLoadingTaskModuleFields === 'pending' ||
					isLoadingEventCategories === 'pending' ? (
						<LoadingSkeleton />
					) : (
						<>
							<div className="grid grid-cols-2 gap-y-4 gap-x-4 text-left">
								{taskModuleFields?.standard
									?.filter(
										(field) => skipTaskModuleKeys.indexOf(field.key) === -1
									)
									?.filter((field) => field?.isDefault)
									?.map((field, fieldId) => {
										return (
											<>
												<Attributes
													key={fieldId}
													field={field}
													onChange={(index, value, fullValue) => {
														if (value || fullValue) {
															let updatedValue = taskForm[index]?.value;

															if (fullValue) {
																updatedValue = fullValue;
															} else {
																if (updatedValue && Array.isArray(value)) {
																	updatedValue = [
																		...(Array.isArray(updatedValue)
																			? updatedValue
																			: [updatedValue]),
																		...value,
																	];
																} else {
																	updatedValue = value;
																}
															}

															setTaskForm({
																...taskForm,
																[index]: {
																	value: updatedValue,
																	fieldId: field.id,
																},
															});
														} else {
															let updated = { ...taskForm };
															updated[index] = {
																...updated[index],
																value: null,
															};

															setTaskForm(updated);
														}
													}}
													form={{
														...(updatedForm?.serviceStart || {}),
														...(updatedForm?.taskSchedule || {}),
														...taskForm,
													}}
													updatedForm={taskForm}
													setForm={setTaskForm}
													moduleId={SERVICE_MODULE_ID}
												/>
											</>
										);
									})}
							</div>

							<div className="col-span-2 flex justify-end mt-8">
								<Button
									onClick={() => {
										let updatedTaskSchedule = updatedForm?.taskSchedule;

										updatedTaskSchedule?.push(taskForm);

										setAddTaskModal(false);
										setUpdateForm({
											...updatedForm,
											taskSchedule: updatedTaskSchedule,
										});
									}}
								>
									Add Task
								</Button>
							</div>
						</>
					)}
				</>
			</Modal>

			<Modal
				title="Add custom field"
				open={isAddCustomAttributesModalOpen}
				onClose={() => setAddCustomAttributesModal(false)}
				size="xl"
			>
				<>
					{isLoadingTaskModuleFields === 'pending' ? (
						<LoadingSkeleton />
					) : (
						<>
							<div className="grid grid-cols-1 gap-y-4 gap-x-4 text-left">
								<TemplateCreateable
									name="Attribute"
									placeholder="Select or type to create..."
									options={moduleFields?.standard
										?.filter((field) => !field?.isDefault)
										.map((field) => ({
											label: field?.label,
											value: field?.id,
										}))}
									onChange={(index, selectedCustomField, raw) => {
										if (raw) {
											setCustomAttributeForm({
												...customAttributeForm,
												...(raw?.__isNew__
													? { label: raw?.label }
													: { id: raw?.value }),
											});
										} else {
											setCustomAttributeForm({});
										}
									}}
									skip={[]}
									value={customAttributeForm?.label || customAttributeForm?.id}
								/>

								{customAttributeForm?.label ? (
									<div className="grid grid-cols-2 gap-y-4">
										<TemplateReactSelect
											name="Type"
											options={FIELD_OPTIONS}
											onChange={(index, type) =>
												setCustomAttributeForm({ ...customAttributeForm, type })
											}
											value={customAttributeForm?.type}
										/>
										<TemplateBoolean
											span={'sm:col-span-1'}
											name="Required"
											className="mr-3"
											onChange={(index, required) =>
												setCustomAttributeForm({
													...customAttributeForm,
													required,
												})
											}
											value={customAttributeForm?.required}
										/>
										<TemplateBoolean
											span={'sm:col-span-1'}
											name="Searchable"
											onChange={(index, searchable) =>
												setCustomAttributeForm({
													...customAttributeForm,
													searchable,
												})
											}
											value={customAttributeForm?.searchable}
										/>

										<TemplateField
											span={'sm:col-span-2'}
											name="Value"
											className="mr-3"
											onChange={(index, value) =>
												setCustomAttributeForm({
													...customAttributeForm,
													value,
												})
											}
											value={customAttributeForm?.value}
										/>
									</div>
								) : null}
							</div>

							<div className="col-span-2 flex justify-end mt-8">
								<Button onClick={() => setAddCustomAttributesModal(false)}>
									Done
								</Button>
							</div>
						</>
					)}
				</>
			</Modal>
		</>
	);
};

export default AddAssetModal;
